import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Datapoint, Filter } from "@/types/dataset";
import { ScrollArea } from "@/components/ui/scroll-area";

const filterSchema = z.object({
  name: z.string().min(1, "Name is required"),
  datapoints: z.array(z.string()).min(1, "Select at least one datapoint"),
});

type FilterFormProps = {
  datapoints: Datapoint[] | undefined;
  onSubmit: (values: z.infer<typeof filterSchema>) => void;
  initialData?: Filter;
};

export const FilterForm = ({ datapoints, onSubmit, initialData }: FilterFormProps) => {
  const form = useForm<z.infer<typeof filterSchema>>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      name: initialData?.name || "",
      datapoints: [],
    },
  });

  // Fetch existing filter datapoints when editing
  const { data: filterDatapoints } = useQuery({
    queryKey: ["filter-datapoints", initialData?.id],
    queryFn: async () => {
      if (!initialData?.id) return [];

      const { data, error } = await supabase
        .from("filter_datapoints")
        .select("datapoint_id")
        .eq("filter_id", initialData.id);

      if (error) throw error;
      return data.map((d) => d.datapoint_id);
    },
    enabled: !!initialData?.id,
  });

  // Set initial datapoints when they are loaded
  useEffect(() => {
    if (filterDatapoints) {
      form.setValue("datapoints", filterDatapoints);
    }
  }, [filterDatapoints, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="datapoints"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Select Datapoints</FormLabel>
              <FormControl>
                <ScrollArea className="h-[300px] border rounded-md p-4">
                  <div className="space-y-2">
                    {datapoints?.map((datapoint) => (
                      <div key={datapoint.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={field.value.includes(datapoint.id)}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...field.value, datapoint.id]
                              : field.value.filter((id) => id !== datapoint.id);
                            field.onChange(newValue);
                          }}
                        />
                        <span>{datapoint.name}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">{initialData ? "Update" : "Create"} Filter</Button>
      </form>
    </Form>
  );
};
